import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from './ui/dialog';
import { Button } from './ui/button';
import { getCsrfToken } from '../utils/apiClient';
import { 
  AlertTriangle, 
  TrendingUp, 
  Calendar, 
  Receipt, 
  FileText,
  Bell,
  DollarSign,
  Clock,
  Sparkles,
  CheckCircle2,
  X
} from 'lucide-react';

// Prompt types and their configurations
const PROMPT_TYPES = {
  OVERDUE_INVOICE: {
    icon: Clock,
    title: 'Overdue Invoice Reminder',
    color: 'text-red-500',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200'
  },
  UPCOMING_BILL: {
    icon: Calendar,
    title: 'Bill Due Soon',
    color: 'text-blue-500',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200'
  },
  HIGH_REVENUE: {
    icon: TrendingUp,
    title: 'Revenue Milestone!',
    color: 'text-green-500',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200'
  },
  TAX_REMINDER: {
    icon: Receipt,
    title: 'Tax Filing Reminder',
    color: 'text-purple-500',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200'
  },
  RECONCILIATION_NEEDED: {
    icon: AlertTriangle,
    title: 'Transactions Need Review',
    color: 'text-orange-500',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200'
  },
  INSIGHT: {
    icon: Sparkles,
    title: 'Financial Insight',
    color: 'text-[#283F8B]',
    bgColor: 'bg-[#283F8B]/5',
    borderColor: 'border-[#283F8B]/20'
  }
};

const ProactivePrompt = ({ prompt, onDismiss, onAction }) => {
  const config = PROMPT_TYPES[prompt.type] || PROMPT_TYPES.INSIGHT;
  const Icon = config.icon;

  return (
    <Dialog open={true} onOpenChange={() => onDismiss(prompt.id)}>
      <DialogContent className={`max-w-md ${config.bgColor} ${config.borderColor} border-2`}>
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl ${config.bgColor} flex items-center justify-center`}>
              <Icon className={`w-6 h-6 ${config.color}`} />
            </div>
            <div>
              <DialogTitle className="text-lg">{config.title}</DialogTitle>
              <DialogDescription className="text-sm">
                {prompt.created_at && new Date(prompt.created_at).toLocaleDateString()}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        
        <div className="py-4">
          <p className="text-foreground">{prompt.message}</p>
          
          {prompt.data && (
            <div className="mt-4 p-3 bg-white/50 rounded-lg">
              {prompt.data.amount && (
                <p className="text-lg font-semibold">{prompt.data.currency_symbol || '$'}{prompt.data.amount.toLocaleString()}</p>
              )}
              {prompt.data.count && (
                <p className="text-sm text-muted-foreground">{prompt.data.count} items</p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="ghost" onClick={() => onDismiss(prompt.id)}>
            Dismiss
          </Button>
          {prompt.action_url && (
            <Button 
              className="bg-[#283F8B] hover:bg-[#283F8B]/90"
              onClick={() => onAction(prompt)}
            >
              {prompt.action_label || 'View Details'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Toast-style notification for non-blocking prompts
const ProactiveToast = ({ prompt, onDismiss, onAction }) => {
  const config = PROMPT_TYPES[prompt.type] || PROMPT_TYPES.INSIGHT;
  const Icon = config.icon;

  return (
    <div 
      className={`fixed bottom-[calc(6rem+env(safe-area-inset-bottom))] lg:bottom-6 right-4 lg:right-6 max-w-sm p-4 rounded-xl shadow-lg border ${config.bgColor} ${config.borderColor} animate-slideIn z-50`}
      data-testid="proactive-toast"
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg ${config.bgColor} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-5 h-5 ${config.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">{config.title}</p>
          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{prompt.message}</p>
          {prompt.action_url && (
            <button 
              className="text-sm text-[#283F8B] font-medium mt-2 hover:underline"
              onClick={() => onAction(prompt)}
            >
              {prompt.action_label || 'View Details'} →
            </button>
          )}
        </div>
        <button 
          className="text-muted-foreground hover:text-foreground"
          onClick={() => onDismiss(prompt.id)}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// Main component that manages proactive prompts
export const ProactivePromptsProvider = ({ children }) => {
  const [prompts, setPrompts] = useState([]);
  const [activePrompt, setActivePrompt] = useState(null);
  const [toastPrompts, setToastPrompts] = useState([]);

  useEffect(() => {
    // Only fetch prompts once per session to avoid spamming users
    const alreadyShown = sessionStorage.getItem('akountfly_prompts_shown');
    if (alreadyShown) return;

    const timer = setTimeout(() => {
      fetchPrompts();
      sessionStorage.setItem('akountfly_prompts_shown', 'true');
    }, 3000); // Delay 3s after login to avoid blocking the UI

    return () => clearTimeout(timer);
  }, []);

  const fetchPrompts = async () => {
    try {
      if (!getCsrfToken()) return;

      const response = await axios.get(`${API}/prompts/active`);
      const newPrompts = response.data;
      
      if (newPrompts.length > 0) {
        // Filter out previously dismissed prompts from this session
        const dismissed = JSON.parse(sessionStorage.getItem('akountfly_dismissed_prompts') || '[]');
        const filtered = newPrompts.filter(p => !dismissed.includes(p.id));
        if (filtered.length > 0) {
          setToastPrompts(filtered.slice(0, 2)); // Max 2 toasts
          setPrompts(filtered);
          
          // Auto-dismiss after 12 seconds
          setTimeout(() => {
            setToastPrompts([]);
          }, 12000);
        }
      }
    } catch (error) {
      // Silently fail - prompts are not critical
    }
  };

  const dismissPrompt = async (promptId) => {
    try {
      await axios.post(`${API}/prompts/${promptId}/dismiss`);
    } catch (error) {
      // Ignore errors
    }
    
    // Persist dismissal in session so it doesn't reappear
    const dismissed = JSON.parse(sessionStorage.getItem('akountfly_dismissed_prompts') || '[]');
    if (!dismissed.includes(promptId)) {
      dismissed.push(promptId);
      sessionStorage.setItem('akountfly_dismissed_prompts', JSON.stringify(dismissed));
    }
    
    // Remove from UI
    setActivePrompt(null);
    setToastPrompts(prev => prev.filter(p => p.id !== promptId));
    setPrompts(prev => prev.filter(p => p.id !== promptId));
  };

  const handleAction = (prompt) => {
    if (prompt.action_url) {
      window.location.href = prompt.action_url;
    }
    dismissPrompt(prompt.id);
  };

  return (
    <>
      {children}
      
      {/* Modal prompt */}
      {activePrompt && (
        <ProactivePrompt 
          prompt={activePrompt} 
          onDismiss={dismissPrompt}
          onAction={handleAction}
        />
      )}
      
      {/* Toast prompts */}
      {!activePrompt && toastPrompts.map((prompt, idx) => (
        <div key={prompt.id} style={{ bottom: `${(idx * 100) + 96}px` }} className="fixed right-4 lg:right-6 z-50">
          <ProactiveToast 
            prompt={prompt}
            onDismiss={dismissPrompt}
            onAction={handleAction}
          />
        </div>
      ))}
    </>
  );
};

export default ProactivePromptsProvider;
